import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'
import { getUserInfo } from 'Utils/Helpers'
import { getSessionToken } from './sessionToken'

import axios from 'axios'

/**
 * @type {{
 *  status: "IDLE" | "UPDATING" | "READY"
 *  previousUpdate: number
 *  currentUpdate: number
 * count: number
 * }}
 */
var REFRESH_TOKEN_CACHE = {
  status: 'IDLE',
  count: 0,
  previousUpdate: 0,
  currentUpdate: 0,
  refreshFailed: false
}

const REFRESH_TOKEN_ENDPOINT = location.hostname.replace('console', 'console.login')

// Token refresh only applies to agentless mode — in agent-connected mode
// (awsurl equals the default agent signin URL) the agent manages token lifecycle
const DEFAULT_AGENT_AWS_URL = 'signin.aws.procyon.ai'

const isAgentlessMode = () => {
  const user = getUserInfo()
  return !!user && user.awsurl !== DEFAULT_AGENT_AWS_URL
}

const resetRefreshCache = () => {
  REFRESH_TOKEN_CACHE.status = 'IDLE'
  REFRESH_TOKEN_CACHE.refreshFailed = false
}

const refreshToken = async () => {
  // If the last refresh attempt failed, don't retry until the cache is reset (4 min window)
  if (REFRESH_TOKEN_CACHE.refreshFailed) {
    throw new Error('Token refresh previously failed — not retrying until cache resets')
  }
  if (REFRESH_TOKEN_CACHE.status === 'IDLE') {
    REFRESH_TOKEN_CACHE.status = 'UPDATING'
    try {
      const user = getUserInfo()
      console.log('Refreshing token for user: ', user)
      const response = await axios.get(
        `https://${REFRESH_TOKEN_ENDPOINT}/refresh?env=${user.ObjectMeta.Tenant}`,
        {
          headers: {
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          withCredentials: true
        }
      )
      REFRESH_TOKEN_CACHE.status = 'READY'
      // After 4 minutes reset to IDLE so token can be refreshed again
      setTimeout(resetRefreshCache, 1000 * 60 * 4)
      return response.data
    } catch (error) {
      // Mark as failed so subsequent 401s don't keep hammering the refresh endpoint
      REFRESH_TOKEN_CACHE.refreshFailed = true
      resetRefreshCache()
      // Auto-clear the failed flag after 4 minutes to allow eventual recovery
      setTimeout(() => { REFRESH_TOKEN_CACHE.refreshFailed = false }, 1000 * 60 * 4)
      throw error
    }
  }

  if (REFRESH_TOKEN_CACHE.status === 'UPDATING') {
    await waitTokenRefresh()
  }
}

const WAIT_TOKEN_TIMEOUT_MS = 10000

const waitTokenRefresh = (params) =>
  new Promise((resolve, reject) => {
    if (REFRESH_TOKEN_CACHE.status !== 'UPDATING') {
      return resolve(params)
    }
    let elapsed = 0
    const timerID = setInterval(() => {
      elapsed += 200
      if (REFRESH_TOKEN_CACHE.status !== 'UPDATING') {
        clearInterval(timerID)
        resolve(params)
      } else if (elapsed >= WAIT_TOKEN_TIMEOUT_MS) {
        // Safety valve: if refresh never completes, unblock after timeout
        clearInterval(timerID)
        resetRefreshCache()
        resolve(params)
      }
    }, 200)
  })

const check401Unauthorized = async () => {
  return await refreshToken()
}

/**
 *
 * @param {import('axios').AxiosRequestConfig} _config
 */
const tokenRequestInterceptor = async (_config) => {
  // Agent-connected mode never triggers refresh, so no need to wait
  if (!isAgentlessMode()) return _config
  return await waitTokenRefresh(_config)
}

/**
 *
 * @param {import('axios').AxiosError} error
 * @param {import('axios').AxiosInstance} axInstance
 * @returns
 */
const tokenResponseInterceptor = (error, axInstance) => {
  return new Promise((resolve, reject) => {
    try {
      // In agent-connected mode, never attempt token refresh — just let the error propagate
      // so other in-flight requests are not blocked by the refresh polling loop
      if (!isAgentlessMode()) {
        return reject(error)
      }

      const status = error?.response?.status
      error.config.__retryCount = error.config.__retryCount || 0

      if (error.config.__retryCount > 3) {
        return reject(error)
      }

      if (status === 401) {
        check401Unauthorized().then(() => {
          error.config.__retryCount += 1
          resolve(axInstance(error.config))
        }).catch(() => reject(error))
      } else {
        reject(error)
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 *  Creates an axios instance with interceptors to handle token refreshing
 * @param {import('axios').AxiosInstance} instance
 */
export const createTokenRefreshingAxiosInstance = (instance) => {
  instance.interceptors.request.use(tokenRequestInterceptor)
  instance.interceptors.response.use(
    (response) => response,
    async (e) => await tokenResponseInterceptor(e, instance)
  )
  return instance
}
